import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { IEventBus } from "@/domains/events"
import { LcAddress } from "@/domains/core"

import type { IVoteStore } from "./interfaces"
import type { Vote } from "./types"
import { VoteAllocatedEvent, VoteRemovedEvent, VoteResetEvent } from "./events"

export class VoteStore implements IVoteStore {
  // Use string keys (LcAddress.toString()) for consistent lowercase lookups
  voteValues: Record<string, Vote> = {}

  constructor(private readonly eventBus: IEventBus) {
    makeObservable(this, {
      voteValues: observable,
      voteCount: computed,
      hasVotesError: computed,
      hasTooManyVotes: computed,
      voteSliders: computed,
      setVoteValue: action,
      removeVote: action,
      resetVotes: action,
      setMaxValue: action,
    })
  }

  get voteCount(): number {
    return this.voteSliders.reduce((current, item) => current + Number(item.value), 0)
  }

  get hasVotesError(): boolean {
    return this.voteCount > 100
  }

  get hasTooManyVotes(): boolean {
    return this.voteSliders.length > 10
  }

  get voteSliders(): Array<Vote> {
    return Object.values(this.voteValues).filter((item) => item.value > 0)
  }

  /**
   * Get the full Vote object for a given address.
   * Returns null if no vote exists for the address.
   * This method encapsulates the internal structure and provides a clean API for components.
   */
  getVote(address: LcAddress): Vote | null {
    return this.voteValues[address.toString()] ?? null
  }

  /**
   * Get the vote value for a given address.
   * Returns null if no vote exists for the address.
   * This method encapsulates the internal structure and provides a clean API for components.
   */
  getVoteValue(address: LcAddress): number | null {
    return this.voteValues[address.toString()]?.value ?? null
  }

  setVoteValue(address: LcAddress, value: number): void {
    // Cap the value at 100%
    const cappedValue = value > 100 ? 100 : value
    const addressKey = address.toString()

    runInAction(() => {
      this.voteValues[addressKey] = { value: cappedValue, address }
    })

    // Convert to viem Address for events (events may be consumed by on-chain interactions)
    this.eventBus.publish(new VoteAllocatedEvent(address.toViemAddress(), cappedValue))
  }

  removeVote(address: LcAddress): void {
    const addressKey = address.toString()

    runInAction(() => {
      this.voteValues[addressKey] = { value: 0, address }
    })

    // Convert to viem Address for events
    this.eventBus.publish(new VoteRemovedEvent(address.toViemAddress()))
  }

  resetVotes(): void {
    runInAction(() => {
      this.voteValues = {}
    })

    this.eventBus.publish(new VoteResetEvent())
  }

  setMaxValue(address: LcAddress): void {
    const addressKey = address.toString()
    const otherVotes = Object.entries(this.voteValues)
      .filter(([key]) => key !== addressKey)
      .reduce((sum, [, vote]) => sum + vote.value, 0)

    const maxValue = 100 - otherVotes

    // Ignore requests to set max when other votes are more than 100%
    if (otherVotes > 100) {
      return
    }

    // Handle when MAX is clicked on a pair that already has votes
    if (this.voteValues[addressKey]) {
      if (maxValue <= 0) {
        // Remove the vote if max value is 0 or negative
        const newVoteValues = { ...this.voteValues }
        delete newVoteValues[addressKey]
        runInAction(() => {
          this.voteValues = newVoteValues
        })
        this.eventBus.publish(new VoteRemovedEvent(address.toViemAddress()))
      } else {
        runInAction(() => {
          this.voteValues[addressKey] = { value: maxValue, address }
        })
        this.eventBus.publish(new VoteAllocatedEvent(address.toViemAddress(), maxValue))
      }
      return
    }

    // Pair has been clicked but does not have any votes yet
    if (!this.voteValues[addressKey]) {
      if (maxValue <= 0) {
        return
      }

      runInAction(() => {
        this.voteValues[addressKey] = { value: maxValue, address }
      })
      this.eventBus.publish(new VoteAllocatedEvent(address.toViemAddress(), maxValue))
      return
    }

    // If the user does not have any votes then fill it with 100
    runInAction(() => {
      this.voteValues = {
        [addressKey]: { value: 100, address },
      }
    })
    this.eventBus.publish(new VoteAllocatedEvent(address.toViemAddress(), 100))
  }
}
